import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Check, Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { REPORT_AI_MESSAGE } from "@/graphql/mutations/sessions";
import { Textarea } from "@/components/ui/textarea";
import { AI_MESSAGE_REPORT_REASONS, MAX_REPORT_COMMENT } from "@/lib/report-reasons";

type Props = {
  messageId: string;
  /** Already reported — restored from `message.reportedAt` across reloads. */
  reported: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onReported: (messageId: string) => void;
};

/**
 * „Pranešti" under one message the AI tutor wrote.
 *
 * The chat is the one place in this app an AI writes to a child directly, so
 * saying „this was not right" has to be two presses away: the flag opens the
 * reasons, and a reason sends. The optional words go in first, above the
 * reasons, so they never cost a third press — a child who has just read
 * something they should not have is not going to write a paragraph about it.
 *
 * Quiet by default, like the question's own report: a faint flag rather than a
 * button, because a prominent one invites a bored child to press it and every
 * report costs a grown-up a real read. Not the question's „Kažkas negerai su
 * šia užduotimi" either — that is about the task, this is about what the AI said.
 */
export function ReportAiMessage({ messageId, reported, open, onOpen, onClose, onReported }: Props) {
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [report] = useMutation(REPORT_AI_MESSAGE);

  if (reported) {
    return (
      <div className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Check className="h-3 w-3" />
        Pranešta — ačiū, suaugęs peržiūrės
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label="Pranešti apie šį AI atsakymą"
        title="Pranešti apie šį AI atsakymą"
        className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-foreground cursor-pointer"
      >
        <Flag className="h-3 w-3" />
        Pranešti
      </button>
    );
  }

  async function send(reason: string) {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await report({
        variables: { messageId, reason, comment: comment.trim() || null },
      });
      const payload = (data as any)?.reportAiMessage;
      if (payload?.success) {
        onReported(messageId);
        onClose();
      } else {
        toast.error(payload?.errors?.[0] || "Nepavyko išsiųsti. Pabandyk dar kartą.");
      }
    } catch {
      // Never an error page in front of a child who was trying to tell us something.
      toast.error("Nepavyko išsiųsti. Pabandyk dar kartą.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="mt-1.5 w-full max-w-[85%] rounded-lg border bg-background/80 p-2.5 space-y-2"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !sending) {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium">
        <Flag className="h-3 w-3" />
        Kas negerai su šiuo atsakymu?
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, MAX_REPORT_COMMENT))}
        placeholder="Gali parašyti, kas negerai (nebūtina)"
        rows={1}
        className="min-h-0 text-xs"
        disabled={sending}
      />
      <div className="flex flex-wrap items-center gap-1.5">
        {AI_MESSAGE_REPORT_REASONS.map((r) => {
          const Icon = r.icon;
          return (
            <button
              key={r.key}
              type="button"
              disabled={sending}
              onClick={() => send(r.key)}
              className="inline-flex items-center gap-1 rounded-full border bg-background px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50 cursor-pointer"
            >
              <Icon className="h-3 w-3 opacity-70" />
              {r.label}
            </button>
          );
        })}
        {sending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        <button
          type="button"
          onClick={onClose}
          disabled={sending}
          className="ml-auto text-xs text-muted-foreground hover:text-foreground cursor-pointer"
        >
          Atšaukti
        </button>
      </div>
    </div>
  );
}
